<template>
  <v-card class="analytics-card" elevation="2">
    <v-card-title class="card-title">
      <div class="title-section">
        <div class="title-text">
          <v-icon color="primary" class="me-2">{{ icon }}</v-icon>
          {{ title }}
        </div>
        <v-btn-toggle
          v-if="shouldShowIntervalToggle"
          v-model="interval"
          mandatory
          color="primary"
          rounded="lg"
          density="compact"
          size="small"
        >
          <v-btn value="hourly" size="x-small">
            <v-icon size="small">mdi-clock-outline</v-icon>
            <span class="ml-1">Hourly</span>
          </v-btn>
          <v-btn value="daily" size="x-small">
            <v-icon size="small">mdi-calendar-today</v-icon>
            <span class="ml-1">Daily</span>
          </v-btn>
          <v-btn value="weekly" size="x-small">
            <v-icon size="small">mdi-calendar-week</v-icon>
            <span class="ml-1">Weekly</span>
          </v-btn>
        </v-btn-toggle>
      </div>
    </v-card-title>
    <v-card-text>
      <div class="sparkline-container">
        <canvas ref="chartCanvas"></canvas>
      </div>
      <div class="stats-container">
        <div v-for="(stat, index) in computedStats" :key="index" class="stat-item">
          <div class="stat-label">{{ stat.label }}</div>
          <div class="stat-value">{{ formatStatValue(stat.value) }}</div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, nextTick, computed } from "vue";
import dayjs from "dayjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler
);

type StatItem = {
  label: string;
  value: string | number;
};

type StatConfig = {
  label: string;
  compute: (data: number[], aggregatedData: Array<{ period: string; value: number }>) => string | number;
};

const props = withDefaults(
  defineProps<{
    title: string;
    icon: string;
    rawData: any[];
    dateField: string;
    valueField: string;
    aggregationMethod?: "sum" | "average" | "none";
    statsConfig: StatConfig[];
    showIntervalToggle?: boolean;
    tooltipFormatter?: (value: number) => string;
  }>(),
  {
    aggregationMethod: "sum",
    showIntervalToggle: false,
  }
);

// Internal interval state
const interval = ref<"hourly" | "daily" | "weekly">("hourly");

const chartCanvas = ref<HTMLCanvasElement | null>(null);
let chartInstance: ChartJS | null = null;

const formatStatValue = (value: string | number): string => {
  if (typeof value === "number") {
    // Check if it's a decimal that should be formatted
    if (value % 1 !== 0) {
      return value.toFixed(1);
    }
    return value.toLocaleString();
  }
  return value;
};

// Helper function to extract numeric value from data item
const getNumericValue = (item: any, field: string): number => {
  const value = item[field];
  if (typeof value === "string") {
    return parseFloat(value) || 0;
  }
  return typeof value === "number" ? value : 0;
};

// Generic aggregation function
const aggregateData = (
  interval: "hourly" | "daily" | "weekly",
  rawData: any[],
  dateField: string,
  valueField: string,
  aggregationMethod: "sum" | "average" | "none"
): Array<{ period: string; value: number }> => {
  if (aggregationMethod === "none" || rawData.length === 0) {
    // Return data as-is, sorted by date
    return rawData
      .map((item) => ({
        period: item[dateField],
        value: getNumericValue(item, valueField),
      }))
      .sort((a, b) => dayjs(a.period).valueOf() - dayjs(b.period).valueOf());
  }

  const grouped: Record<string, { values: number[]; count: number }> = {};

  rawData.forEach((item) => {
    let key: string;
    const date = dayjs(item[dateField]);

    switch (interval) {
      case "daily":
        key = date.format("YYYY-MM-DD");
        break;
      case "weekly":
        // Group by week start (Monday)
        key = date.startOf("week").format("YYYY-MM-DD");
        break;
      case "hourly":
      default:
        key = item[dateField];
        break;
    }

    if (!grouped[key]) {
      grouped[key] = { values: [], count: 0 };
    }

    const value = getNumericValue(item, valueField);
    grouped[key].values.push(value);
    grouped[key].count += 1;
  });

  // Convert to sorted array with aggregated values
  return Object.entries(grouped)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([period, data]) => {
      let aggregatedValue: number;
      if (aggregationMethod === "average") {
        aggregatedValue =
          data.values.length > 0
            ? data.values.reduce((sum, val) => sum + val, 0) / data.values.length
            : 0;
      } else {
        // sum
        aggregatedValue = data.values.reduce((sum, val) => sum + val, 0);
      }
      return {
        period,
        value: aggregatedValue,
      };
    });
};

// Computed aggregated data
const aggregatedData = computed(() =>
  aggregateData(interval.value, props.rawData, props.dateField, props.valueField, props.aggregationMethod)
);

// Computed chart data
const chartData = computed(() => aggregatedData.value.map((item) => item.value));

// Computed chart labels
const chartLabels = computed(() => {
  return aggregatedData.value.map((item) => {
    const date = dayjs(item.period);
    switch (interval.value) {
      case "daily":
        return date.format("MMM D");
      case "weekly":
        return `Week of ${date.format("MMM D")}`;
      case "hourly":
      default:
        // Check if it's already a formatted datetime string or needs formatting
        if (item.period.includes(" ")) {
          return dayjs(item.period).format("MMM D, h:mm A");
        }
        return date.format("MMM D HH:mm");
    }
  });
});

// Computed statistics
const computedStats = computed(() => {
  return props.statsConfig.map((config) => ({
    label: config.label,
    value: config.compute(chartData.value, aggregatedData.value),
  }));
});

// Determine if interval toggle should be shown
const shouldShowIntervalToggle = computed(() => {
  return props.showIntervalToggle || props.aggregationMethod !== "none";
});

const createChart = () => {
  nextTick(() => {
    // Destroy existing chart if it exists
    if (chartInstance) {
      chartInstance.destroy();
    }

    // Create chart
    if (chartCanvas.value && chartData.value.length > 0) {
      chartInstance = new ChartJS(chartCanvas.value, {
        type: "line",
        data: {
          labels: chartLabels.value,
          datasets: [
            {
              label: props.title,
              data: chartData.value,
              borderColor: "rgb(25, 118, 210)",
              backgroundColor: "rgba(25, 118, 210, 0.1)",
              tension: 0.4,
              fill: true,
              pointRadius: 2,
              pointHoverRadius: 4,
              borderWidth: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              mode: "index",
              intersect: false,
              callbacks: props.tooltipFormatter
                ? {
                    label: (context) => props.tooltipFormatter!(context.parsed.y ?? 0),
                  }
                : undefined,
            },
          },
          scales: {
            x: {
              display: false,
            },
            y: {
              display: false,
              beginAtZero: true,
            },
          },
          interaction: {
            mode: "nearest",
            axis: "x",
            intersect: false,
          },
        },
      });
    }
  });
};

// Watch for data changes and recreate chart
watch(
  () => [chartData.value, chartLabels.value, interval.value],
  () => {
    createChart();
  },
  { deep: true }
);

onMounted(() => {
  createChart();
});

onUnmounted(() => {
  if (chartInstance) {
    chartInstance.destroy();
  }
});
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.analytics-card {
  background-color: white;
  border-radius: 8px;

  .card-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 600;
    font-size: 1.1rem;
    color: $gray-800;
    padding: 16px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);

    .title-section {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      gap: 16px;

      @include breakpoints.media-breakpoint-down(md) {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
      }

      .title-text {
        display: flex;
        align-items: center;
      }
    }
  }

  :deep(.v-card-text) {
    padding: 20px;
  }
}

.sparkline-container {
  margin-bottom: 24px;
  padding: 12px;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;

  canvas {
    width: 100% !important;
    height: 100% !important;
  }
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @include breakpoints.media-breakpoint-down(sm) {
    grid-template-columns: 1fr;
  }
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .stat-label {
    font-size: 0.75rem;
    color: $gray-600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
  }

  .stat-value {
    font-size: 1.25rem;
    font-weight: 600;
    color: $primary;
  }
}
</style>

